import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MultiSelect } from '@mantine/core'

import styles from '../VolunteeringFiltersModal.module.scss'
import { FilterProps } from '../VolunteeringFiltersModal.types'

import { Box, Typography, Icon, Loader, FakeCheckbox, Flex } from '@percent/lemonade'
import { getVolunteeringSkills } from '@percent/workplace-giving/api/volunteering/getVolunteeringSkills/getVolunteeringSkills'
import { useQuery } from '@percent/workplace-giving/common/hooks'

export function SkillsFilter({ selectedFilters, setSelectedFilters }: FilterProps) {
  const { t } = useTranslation()

  const [dropdownOpened, setDropdownOpened] = useState(false)

  const { data: skillsData, isLoading } = useQuery(['getVolunteeringSkills'], getVolunteeringSkills)

  const skillsOptions = useMemo(() => {
    if (skillsData) {
      return skillsData
        .map(skill => ({
          value: skill.id,
          label: skill.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    return []
  }, [skillsData])

  if (isLoading) {
    return <Loader />
  }

  return (
    <Box>
      <Typography className={styles.FiltersSectionTitle}>
        {t('workplace_giving.volunteering.filters.skills.label')}
      </Typography>
      <MultiSelect
        dropdownOpened={dropdownOpened}
        comboboxProps={{ withinPortal: false }}
        wrapperProps={{ 'data-testid': 'skillsSelect' }}
        style={{ width: '100%' }}
        maxDropdownHeight={150}
        placeholder={
          !selectedFilters.relevantSkills?.length
            ? (t('workplace_giving.volunteering.filters.skills.placeholder') as string)
            : ''
        }
        data={skillsOptions}
        value={selectedFilters.relevantSkills as string[]}
        onChange={selectedValues => {
          setSelectedFilters({ ...selectedFilters, relevantSkills: selectedValues })
        }}
        leftSectionProps={{
          onClick: () => {
            setDropdownOpened(!dropdownOpened)
          }
        }}
        onDropdownOpen={() => setDropdownOpened(true)}
        onDropdownClose={() => setDropdownOpened(false)}
        rightSectionPointerEvents="none"
        rightSection={
          <Box className={styles.FiltersDropdownRightSectionWrapper}>
            {selectedFilters.relevantSkills?.length ? (
              <button
                type="button"
                data-testid="clearSkillsFilter"
                onClick={() => {
                  setSelectedFilters({ ...selectedFilters, relevantSkills: [] })
                }}
              >
                <Icon name="close" size={4} color="gray600" />
              </button>
            ) : null}

            <button
              type="button"
              data-testid="toggleSkillsFilter"
              onClick={() => {
                setDropdownOpened(!dropdownOpened)
              }}
            >
              <Icon name={dropdownOpened ? 'dropdown-arrow-up' : 'dropdown-arrow-down'} size={8} color="gray600" />
            </button>
          </Box>
        }
        renderOption={item => (
          <Flex justify="center" align="center">
            <FakeCheckbox isSelected={!!item.checked} />
            <div style={{ marginLeft: '0.5rem' }}>{item.option.label}</div>
          </Flex>
        )}
      />
    </Box>
  )
}
